// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { CustomSelect } from "../../components/CustomFields/CustomSelect";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../components/CustomFields/TextField";

// IMPORT SERVICE
import { AlertType } from "../../services/models/AlertType";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import {
  GetHospitalDetail,
  CreateHospital,
  UpdateHospital,
} from "../../services/HospitalService";
import { GetCountryListForSelect } from "../../services/CountryService";
import { GetCountryRegionListForSelect } from "../../services/CountryRegionService";
import { PreferencesContext } from "../../PreferenceContext";
import CustomBreadcrumbs from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import PhoneInput from "react-phone-number-input";
import CustomTooltip from "../../components/ToolTip/CustomToolTip";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

export default function HospitalSave(Props: any) {
  const { isForPopUp = false, handleClosePopUp, name } = Props;
  // SCHEMA FOR OPTION
  interface selectOption {
    label: string;
    value: string;
  }

  // SCHEMA FOR FORM
  interface formInitialValueTypes {
    hospitalId: number;
    consumerId: number;
    orgId: number;
    branchId: number;
    code: string;
    hospitalName: string;
    contactPerson: string;
    address1: string;
    address2: string;
    location: string;
    city: string;
    postalCode: string;
    countryId: selectOption | null;
    countryName: string;
    countryRegionId: selectOption | null;
    countryRegionName: string;
    email: string;
    primaryMobileNumber: string;
    secondaryMobileNumber: string;
    isDeleted: boolean;
  }

  // DEFAULT SCHEMA VALUE FOR FORM
  const initHospitalData: formInitialValueTypes = {
    hospitalId: 0,
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    code: "",
    hospitalName: isForPopUp ? name?.toUpperCase() : "",
    contactPerson: "",
    address1: "",
    address2: "",
    location: "",
    city: "",
    postalCode: "",
    countryId: { label: "INDIA", value: "91" },
    countryName: "",
    countryRegionId: { label: "Gujarat (24)", value: "7" },
    countryRegionName: "",
    email: "",
    primaryMobileNumber: "",
    secondaryMobileNumber: "",
    isDeleted: false,
  };

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();

  let curRawHospitalId = urlParams.get("q");
  let curHospitalId = 0;
  if (
    curRawHospitalId !== null &&
    curRawHospitalId !== undefined &&
    isForPopUp == false
  ) {
    let decId = edService.decrypt(curRawHospitalId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      curHospitalId = +decId;
    }
  } else {
    if (urlParams.size > 0 && isForPopUp === false) {
      navigate("/not-found");
    }
  }

  // STATES FOR DATA STORAGE  -- PLEASE USE PROPER TYPES FOR STATE
  const [countryRegionData, setCountryRegionData] = useState<any[]>([]); // REPLACE ANY WITH COUNTRY INTERFACE
  const [countryData, setCountryData] = useState<any[]>([]); // REPLACE ANY WITH STATE INTERFACE

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  const [seeMore, setSeeMore] = useState<boolean>(false);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    hospitalId: Yup.number().nullable(),
    branchId: Yup.number().required(),
    orgId: Yup.number().required(),
    consumerId: Yup.number().required(),
    code: Yup.string(),
    hospitalName: Yup.string().required("Hospital Name is required"),
    contactPerson: Yup.string().nullable(),
    email: Yup.string().email("Invalid email").nullable(),
    location: Yup.string().nullable(),
    address1: Yup.string().nullable(),
    address2: Yup.string(),
    city: Yup.string().nullable(),
    postalCode: Yup.string().nullable(),
    countryId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    countryName: Yup.string().nullable(),
    countryRegionId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    countryRegionName: Yup.string().nullable(),
    primaryMobileNumber: Yup.string().nullable(),
    secondaryMobileNumber: Yup.string().nullable(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initHospitalData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // GET Hospital DETAIL
  const getHospitalDetailById = async () => {
    let lHospital: any;
    let params = new URLSearchParams();

    params.append("hospitalId", curHospitalId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("organizationId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());

    try {
      const res: any = await GetHospitalDetail(params);
      lHospital = res.data.data;
      methods.reset({
        ...initHospitalData,
        hospitalId: lHospital?.hospitalId || 0,
        code: lHospital?.code || "",
        hospitalName: lHospital?.hospitalName || "",
        contactPerson: lHospital?.contactPerson || "",
        email: lHospital?.email || "",
        address1: lHospital?.address1 || "",
        address2: lHospital?.address2 || "",
        city: lHospital?.city || "",
        postalCode: lHospital?.postalCode || "",
        countryId: lHospital?.countryId
          ? {
            label: lHospital?.countryName || "",
            value: lHospital?.countryId?.toString(),
          }
          : null,
        countryName: lHospital?.countryName || "",
        countryRegionId: lHospital?.countryRegionId
          ? {
            label: lHospital?.countryRegionName,
            value: lHospital?.countryRegionId?.toString(),
          }
          : null,
        countryRegionName: lHospital?.countryRegionName || "",
        location: lHospital?.location || "",
        primaryMobileNumber: lHospital?.primaryMobileNumber || "",
        secondaryMobileNumber: lHospital?.secondaryMobileNumber || "",
      });
      return lHospital;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE BRANCH
  const saveHospital = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        hospitalId: value.hospitalId,
        branchId: userContext?.branchId,
        organizationId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        code: value.code?.trim() || "",
        hospitalName: value.hospitalName?.trim() || "",
        contactPerson: value.contactPerson?.trim() || "",
        email: value.email?.trim() || "",
        address1: value.address1?.trim() || "",
        address2: value.address2?.trim() || "",
        location: value.location?.trim() || "",
        city: value.city?.trim() || "",
        postalCode: value.postalCode?.trim() || "",
        countryRegionId: value.countryRegionId?.value,
        countryId: value.countryId?.value,
        countryName: value?.countryName || "",
        countryRegionName: value?.countryRegionName || "",
        primaryMobileNumber: value?.primaryMobileNumber?.trim() || "",
        secondaryMobileNumber: value?.secondaryMobileNumber?.trim() || "",
        DataManagedBy: userContext.loginDetail.userId,
      };
      if (curHospitalId > 0) {
        result = await UpdateHospital(formData);
      } else {
        result = await CreateHospital(formData);
      }
      if (result?.data) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result.data) {
            handleClosePopUp("NAME_OF_HOSPITAL", result.data);
          }
        }
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET COUNTRIES
  async function fetchCountry() {
    try {
      let params = new URLSearchParams();
      const countryData: any = await GetCountryListForSelect(params);
      if (countryData?.data?.data) {
        let countyCollection = countryData.data.data;
        setCountryData(countyCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // FETCH STATE FOR COUNTRY
  async function fetchCountryRegionData(countryId) {
    try {
      let params = new URLSearchParams();
      params.append("countryId", countryId);
      const localCountryRegionData: any = await GetCountryRegionListForSelect(
        params
      );
      if (localCountryRegionData?.data?.data) {
        let stateCollection = localCountryRegionData.data.data;
        setCountryRegionData(stateCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveHospital(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };
  // FETCH DATA
  const fetchData = async () => {
    try {
      await fetchCountry();
      if (curHospitalId > 0) {
        const Hospital = await getHospitalDetailById();
        if (Hospital?.countryId) {
          await fetchCountryRegionData(Hospital?.countryId);
        }
      } else {
        await fetchCountryRegionData(methods.getValues()?.countryId?.value);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curHospitalId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className="main-container" pl={0}>
        {isForPopUp === false && (
          <>
            <Grid container className="page-header">
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  <CustomBreadcrumbs />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Basic Information</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.hospitalName
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Hospital Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          autoFocus
                          {...methods.register("hospitalName", {
                            onChange: (e) => {
                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              // e.target.value = e.target.value
                              //   ? e.target.value.toUpperCase()
                              //   : "";
                              methods.setValue("hospitalName", e.target.value, {
                                shouldValidate: true,
                              }); // UPDATE VALUE
                            },
                          })}
                          error={!!methods.formState.errors.hospitalName}
                          className={`text-input ${methods.formState.errors.hospitalName ? "error" : ""
                            }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.hospitalName?.message}
                    </Typography>
                  </Grid>
                  {/* <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.contactPerson
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Contact Person
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          autoFocus={methods.watch("hospitalName") !== ""}
                          {...methods.register("contactPerson")}
                          error={!!methods.formState.errors.contactPerson}
                          className={`text-input ${
                            methods.formState.errors.contactPerson
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.contactPerson?.message}
                    </Typography>
                  </Grid> */}
                </Grid>
              </Grid>
              <Grid item xs={12} className={"group-container"}>
                <Typography
                  className="font-size-16 text-link"
                  onClick={(e) => {
                    setSeeMore(!seeMore);
                  }}
                >
                  {seeMore ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography>See Less</Typography>
                      <ExpandLess />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography>See More</Typography>
                      <ExpandMore />
                    </Box>
                  )}
                </Typography>
              </Grid>
              {seeMore && (
                <Grid item xs={12} className="group-container">
                  <Grid container item spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6">Contact Information</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${methods.formState.errors.contactPerson
                            ? "validation-message"
                            : ""
                            }`}
                        >
                          Contact Person
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            autoFocus={methods.watch("hospitalName") !== ""}
                            {...methods.register("contactPerson")}
                            error={!!methods.formState.errors.contactPerson}
                            className={`text-input ${methods.formState.errors.contactPerson
                              ? "error"
                              : ""
                              }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.contactPerson?.message}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}></Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${methods.formState.errors.location
                            ? "validation-message"
                            : ""
                            }`}
                        >
                          Location
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("location")}
                            error={!!methods.formState.errors.location}
                            className={`text-input ${methods.formState.errors.location ? "error" : ""
                              }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.location?.message}
                      </Typography>
                    </Grid>
                    {/* <Grid item xs={12} sm={9} md={9} /> */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${methods.formState.errors.address1
                            ? "validation-message"
                            : ""
                            }`}
                        >
                          Address 1{" "}
                          <CustomTooltip
                            title="This field is important to be filled for better communication"
                            hideHeader={true}
                          ></CustomTooltip>
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("address1")}
                            error={!!methods.formState.errors.address1}
                            className={`text-input ${methods.formState.errors.address1 ? "error" : ""
                              }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.address1?.message}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${methods.formState.errors.address2
                            ? "validation-message"
                            : ""
                            }`}
                        >
                          Address 2{" "}
                          <CustomTooltip
                            title="This field is important to be filled for better communication"
                            hideHeader={true}
                          ></CustomTooltip>
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("address2")}
                            error={!!methods.formState.errors.address2}
                            className={`text-input ${methods.formState.errors.address2 ? "error" : ""
                              }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.address2?.message}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${methods.formState.errors.city
                            ? "validation-message"
                            : ""
                            }`}
                        >
                          City{" "}
                          <CustomTooltip
                            title="This field is important to be filled for better communication"
                            hideHeader={true}
                          ></CustomTooltip>
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("city")}
                            error={!!methods.formState.errors.city}
                            className={`text-input ${methods.formState.errors.city ? "error" : ""
                              }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.city?.message}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${methods.formState.errors.postalCode
                            ? "validation-message"
                            : ""
                            }`}
                        >
                          Postal Code
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            id="postalCode"
                            size="small"
                            {...methods.register("postalCode")}
                            error={!!methods.formState.errors.postalCode}
                            className={`text-input ${methods.formState.errors.postalCode ? "error" : ""
                              }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.postalCode?.message}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${methods.formState.errors.countryId
                            ? "validation-message"
                            : ""
                            }`}
                        >
                          Country
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="countryId"
                            render={({ field }) => (
                              <CustomSelect
                                {...field}
                                isNotCreatable={true}
                                id={"countryId"}
                                FieldName={"countryId"}
                                options={countryData}
                                openMenuOnFocus
                                isClearable
                                isSearchable
                                menuPortalTarget={document.querySelector("body")}
                                handleChange={async (e) => {
                                  if (
                                    methods
                                      ?.getValues()
                                      ?.countryId?.value?.toString() !==
                                    e?.value?.toString()
                                  ) {
                                    methods.setValue(
                                      "countryId",
                                      e?.value ? e : null,
                                      {
                                        shouldValidate: true,
                                      }
                                    );
                                    methods.setValue(
                                      "countryName",
                                      e?.value ? e?.label : null,
                                      {
                                        shouldValidate: true,
                                      }
                                    );
                                    methods.setValue("countryRegionId", null, {
                                      shouldValidate: true,
                                    });
                                    methods.setValue("countryRegionName", "", {
                                      shouldValidate: true,
                                    });
                                    if (e?.value !== null) {
                                      setIsLoading(true);
                                      await fetchCountryRegionData(e?.value);
                                      setIsLoading(false);
                                    } else {
                                      setCountryRegionData([]);
                                    }
                                  }
                                }}
                                className={`text-input ${methods.formState.errors.postalCode
                                  ? "error"
                                  : ""
                                  }`}
                              />
                            )}
                          />
                        </SkeletonWrapper>
                        <Typography className="validation-message">
                          {methods.formState.errors.countryId?.message}
                        </Typography>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${methods.formState.errors.countryRegionId
                            ? "validation-message"
                            : ""
                            }`}
                        >
                          State
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <Controller
                            control={methods.control}
                            name="countryRegionId"
                            render={({ field }) => (
                              <CustomSelect
                                {...field}
                                isNotCreatable={true}
                                id={"countryRegionId"}
                                FieldName={"countryRegionId"}
                                options={countryRegionData}
                                openMenuOnFocus
                                isClearable
                                isSearchable
                                menuPortalTarget={document.querySelector("body")}
                                handleChange={(e) => {
                                  methods.setValue(
                                    "countryRegionId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue(
                                    "countryRegionName",
                                    e?.value ? e?.label : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                }}
                                className={`text-input ${methods.formState.errors.countryId
                                  ? "error"
                                  : ""
                                  }`}
                              />
                            )}
                          />
                        </SkeletonWrapper>
                        <Typography className="validation-message">
                          {methods.formState.errors.countryRegionId?.message}
                        </Typography>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <FormLabel
                          className={`fileds-label-text  ${methods.formState.errors.email
                            ? "validation-message"
                            : ""
                            }`}
                        >
                          Email{" "}
                          <CustomTooltip
                            title="This field is important to be filled for better communication"
                            hideHeader={true}
                          ></CustomTooltip>
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <CustomTextField
                            placeholder=""
                            autoCapitalize="none"
                            type="text"
                            fullWidth
                            size="small"
                            {...methods.register("email")}
                            error={!!methods.formState.errors.email}
                            className={`text-input ${methods.formState.errors.email ? "error" : ""
                              }`}
                          />
                        </SkeletonWrapper>
                      </FormControl>
                      <Typography className="validation-message">
                        {methods.formState.errors.email?.message}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <div className="input-group">
                        <FormLabel
                          className={`fileds-label-text  ${methods.formState.errors.primaryMobileNumber
                            ? "validation-message"
                            : ""
                            }`}
                        >
                          Primary Mobile Number{" "}
                          <CustomTooltip
                            title="This field is important to be filled for better communication"
                            hideHeader={true}
                          ></CustomTooltip>
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <PhoneInput
                            id="primaryMobileNumber"
                            international
                            countryCallingCodeEditable={false}
                            defaultCountry="IN"
                            {...methods.register("primaryMobileNumber", {
                              shouldUnregister: true,
                            })}
                            error={
                              methods.formState.errors.primaryMobileNumber
                                ?.message
                            }
                            className={`phone-input ${methods.formState.errors.primaryMobileNumber
                              ? "error"
                              : ""
                              }`}
                            onChange={(e) => {
                              methods.setValue("primaryMobileNumber", e || "");
                            }}
                            value={methods.getValues()?.primaryMobileNumber || ""}
                            placeholder="Enter phoneś number"
                          />
                        </SkeletonWrapper>
                        <Typography className="validation-message">
                          {methods.formState.errors.primaryMobileNumber?.message}
                        </Typography>
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <div className="input-group">
                        <FormLabel
                          className={`fileds-label-text  ${methods.formState.errors.secondaryMobileNumber
                            ? "validation-message"
                            : ""
                            }`}
                        >
                          Secondary Mobile Number{" "}
                          <CustomTooltip
                            title="This field is important to be filled for better communication"
                            hideHeader={true}
                          ></CustomTooltip>
                        </FormLabel>
                        <SkeletonWrapper
                          isLoading={showSkeleton}
                          className="skeleton-input"
                        >
                          <PhoneInput
                            id="secondaryMobileNumber"
                            international
                            countryCallingCodeEditable={false}
                            defaultCountry="IN"
                            {...methods.register("secondaryMobileNumber")}
                            error={
                              methods.formState.errors.secondaryMobileNumber
                                ?.message
                            }
                            className={`phone-input ${methods.formState.errors.secondaryMobileNumber
                              ? "error"
                              : ""
                              }`}
                            value={
                              methods.getValues()?.secondaryMobileNumber || ""
                            }
                            onChange={(e) => {
                              methods.setValue("secondaryMobileNumber", e || "");
                            }}
                            placeholder="Enter phone number"
                          />
                        </SkeletonWrapper>
                        <Typography className="validation-message">
                          {
                            methods.formState.errors.secondaryMobileNumber
                              ?.message
                          }
                        </Typography>
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton || isLoading}
                  >
                    {curHospitalId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
